import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import styled from "styled-components";

import { Icon } from "src/shared/components";
import { deviceBreakpoints } from "src/theme/deviceBreakpoints";
import {
  ProfileDisplayDetails,
  ProfileDisplayIdentity,
  ProfileDisplayLayout,
} from "src/views/hacker-directory/shared/ProfileDisplay";
import { DiscoveryProfile } from "src/views/hacker-directory/shared/types";

const MODAL_ANIMATION_MS = 180;
const MODAL_COMPACT_BREAKPOINT = 700;

const ProfileModal: React.FC<{
  profile: DiscoveryProfile;
  onClose: () => void;
}> = ({ profile, onClose }) => {
  const [isOpen, setIsOpen] = useState(true);
  const title = profile.name || "Hacker profile";
  const requestClose = () => setIsOpen(false);

  // Lock background scrolling and compensate for the removed scrollbar to
  // prevent the page from shifting horizontally while the modal is open.
  useEffect(() => {
    const body = document.body;
    const previousOverflow = body.style.overflow;
    const previousPaddingRight = body.style.paddingRight;
    const currentPaddingRight = parseFloat(
      window.getComputedStyle(body).paddingRight
    );
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`;
    }

    return () => {
      body.style.overflow = previousOverflow;
      body.style.paddingRight = previousPaddingRight;
    };
  }, []);

  return (
    <StyledModal
      isOpen={isOpen}
      closeTimeoutMS={MODAL_ANIMATION_MS}
      onAfterClose={onClose}
      onRequestClose={requestClose}
      shouldCloseOnEsc
      shouldCloseOnOverlayClick
      shouldReturnFocusAfterClose
      preventScroll
      contentLabel={`${title}'s full profile`}
      overlayElement={(props, contentElement) => (
        <Overlay {...props}>{contentElement}</Overlay>
      )}
      style={{
        overlay: {
          position: "fixed",
          zIndex: 1000,
          inset: 0,
          display: "flex",
          padding: "14px",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(0, 0, 0, 0.3)",
        },
      }}
    >
      <CloseButton aria-label="Close profile" onClick={requestClose}>
        <Icon name="x" width="20px" height="20px" color="textSecondary" />
      </CloseButton>

      <ProfileDisplayLayout
        breakpoints={{
          details: {
            stackedLayout: deviceBreakpoints.medium,
            compactDetails: MODAL_COMPACT_BREAKPOINT,
          },
          identity: {
            sideBySide: [
              {
                minWidth: deviceBreakpoints.tablet,
                maxWidth: deviceBreakpoints.medium,
              },
            ],
          },
        }}
      >
        <ProfileDisplayDetails profile={profile} />
        <ProfileDisplayIdentity profile={profile} connectSlack />
      </ProfileDisplayLayout>
    </StyledModal>
  );
};

const Overlay = styled.div`
  opacity: 0;
  transition: opacity ${MODAL_ANIMATION_MS}ms ease;

  &.ReactModal__Overlay--after-open {
    opacity: 1;
  }

  &.ReactModal__Overlay--before-close {
    opacity: 0;
  }
`;

const StyledModal = styled(Modal)`
  position: relative;
  box-sizing: border-box;
  width: min(1296px, calc(100vw - 64px));
  max-height: calc(100vh - 64px);
  padding: 56px;
  overflow-y: auto;
  border-radius: 8px;
  background: ${({ theme }) => theme.globalConstants.color.white};
  outline: none;
  opacity: 0;
  transform: translateY(8px) scale(0.99);
  transition: opacity ${MODAL_ANIMATION_MS}ms ease,
    transform ${MODAL_ANIMATION_MS}ms ease;

  &.ReactModal__Content--after-open {
    opacity: 1;
    transform: translateY(0) scale(1);
  }

  &.ReactModal__Content--before-close {
    opacity: 0;
    transform: translateY(8px) scale(0.99);
  }

  @media (max-width: ${MODAL_COMPACT_BREAKPOINT}px) {
    width: calc(100vw - 28px);
    max-height: calc(100vh - 28px);
    padding: 32px 24px 24px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  z-index: 1;
  top: 17px;
  right: 16px;
  display: flex;
  width: 32px;
  height: 32px;
  padding: 0;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.globalConstants.color.focus};
  }

  @media (max-width: ${MODAL_COMPACT_BREAKPOINT}px) {
    top: 8px;
    right: 8px;
    width: 24px;
    height: 24px;

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

export default ProfileModal;
